/**
 * PIEZA 6 — Onboarding asistido: contrato (PDF) → hitos de pago con el copiloto.
 * El usuario elige un contrato en PDF de la oportunidad e indica la base
 * imponible del proyecto; la IA propone un plan de hitos de pago. El usuario
 * revisa/edita en split-screen y, al aprobar, se crean los hitos reales con
 * POST /milestones.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import Modal from '../ui/Modal'
import {
  type DocumentListItem,
  type MilestoneItemInput,
  type SuggestMilestonesResponse,
} from '@toga/shared'

const inputCls =
  'w-full px-2 py-1 rounded bg-[var(--bg-input-soft)] border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

interface Row {
  title: string
  amountEur: string
  date: string
}

interface Props {
  open: boolean
  onClose: () => void
  clientId: string
  opportunityId: string
  onCreated: () => void
}

export default function SuggestMilestonesModal({
  open,
  onClose,
  clientId,
  opportunityId,
  onCreated,
}: Props) {
  const { t } = useTranslation(['billing', 'common'])
  const [docs, setDocs] = useState<DocumentListItem[]>([])
  const [docsLoading, setDocsLoading] = useState(false)
  const [documentId, setDocumentId] = useState('')
  const [amountEur, setAmountEur] = useState('')

  const [suggesting, setSuggesting] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiNotConfigured, setAiNotConfigured] = useState(false)
  const [notes, setNotes] = useState<string | null>(null)

  const [rows, setRows] = useState<Row[]>([])
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Carga de documentos PDF de la oportunidad al abrir.
  useEffect(() => {
    if (!open) return
    // Reset al abrir.
    setDocumentId('')
    setAmountEur('')
    setAiError(null)
    setAiNotConfigured(false)
    setNotes(null)
    setRows([])
    setCreateError(null)

    let cancelled = false
    setDocsLoading(true)
    api
      .get<{ items: DocumentListItem[] }>(`/documents?opportunityId=${opportunityId}`)
      .then((res) => {
        if (cancelled) return
        const pdfs = res.items.filter((d) => d.latestMimeType === 'application/pdf')
        setDocs(pdfs)
        const only = pdfs[0]
        if (pdfs.length === 1 && only) setDocumentId(only.id)
      })
      .catch((err) => {
        if (cancelled) return
        const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
        setAiError(body?.error ?? (err instanceof Error ? err.message : t('suggestMilestones.errors.loadDocs')))
      })
      .finally(() => {
        if (!cancelled) setDocsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, opportunityId, t])

  const dealAmountCents = parseEurosToCents(amountEur)

  const onSuggest = async () => {
    if (!documentId || dealAmountCents <= 0) return
    setSuggesting(true)
    setAiError(null)
    setAiNotConfigured(false)
    setNotes(null)
    try {
      const res = await api.post<SuggestMilestonesResponse>(
        `/opportunities/${opportunityId}/suggest-milestones`,
        { documentId, dealAmountCents }
      )
      setNotes(res.notes)
      setRows(
        res.items.map((it) => ({
          title: it.title,
          amountEur: centsToEurosInput(it.amountCents),
          date: it.dueDate ? it.dueDate.slice(0, 10) : '',
        }))
      )
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { code?: string; error?: string }) : undefined
      if (body?.code === 'AI_NOT_CONFIGURED') {
        setAiNotConfigured(true)
      } else {
        setAiError(body?.error ?? (err instanceof Error ? err.message : t('suggestMilestones.errors.suggest')))
      }
    } finally {
      setSuggesting(false)
    }
  }

  const updateRow = (i: number, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  }

  const addRow = () => {
    setRows((rs) => [...rs, { title: '', amountEur: '', date: '' }])
  }

  const removeRow = (i: number) => {
    setRows((rs) => rs.filter((_, j) => j !== i))
  }

  const sumCents = useMemo(
    () => rows.reduce((acc, r) => acc + parseEurosToCents(r.amountEur), 0),
    [rows]
  )

  // Tolerancia de 1 € (100 cént.) por redondeos al repartir un importe en hitos.
  const amountOk = dealAmountCents > 0 && Math.abs(sumCents - dealAmountCents) <= 100
  const canCreate = rows.length > 0 && amountOk && !creating

  const onCreate = async () => {
    if (!canCreate) return
    setCreating(true)
    setCreateError(null)
    try {
      const items: MilestoneItemInput[] = rows.map((r, i) => ({
        position: i,
        title: r.title.trim() || t('suggestMilestones.milestoneDefault', { n: i + 1 }),
        amountCents: parseEurosToCents(r.amountEur),
        vatRate: '0.21',
        dueDate: r.date ? new Date(`${r.date}T12:00:00`).toISOString() : new Date().toISOString(),
      }))
      await api.post('/milestones', { clientId, opportunityId, quoteId: null, items })
      onCreated()
      onClose()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setCreateError(body?.error ?? (err instanceof Error ? err.message : t('suggestMilestones.errors.create')))
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('suggestMilestones.title')}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Entrada (arriba) */}
        <div className="surface p-4 space-y-3">
          <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
            <Sparkles size={15} className="text-toga-700 dark:text-toga-300" />
            {t('suggestMilestones.contractAndAmount')}
          </h3>

          {docsLoading ? (
            <p className="text-xs t-faint flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> {t('suggestMilestones.loadingDocs')}
            </p>
          ) : docs.length === 0 ? (
            <p className="text-xs t-faint">
              {t('suggestMilestones.uploadFirst')}
            </p>
          ) : (
            <label className="block space-y-1">
              <span className="text-xs t-secondary">{t('suggestMilestones.contractPdf')}</span>
              <select
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                className={inputCls}
              >
                <option value="">{t('suggestMilestones.selectContract')}</option>
                {docs.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.fileName} (v{d.latestVersion})
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block space-y-1">
            <span className="text-xs t-secondary">{t('suggestMilestones.projectAmount')}</span>
            <input
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              placeholder={t('suggestMilestones.amountPlaceholder')}
              className={`${inputCls} text-right`}
              inputMode="decimal"
            />
          </label>

          <button
            type="button"
            disabled={!documentId || dealAmountCents <= 0 || suggesting}
            onClick={() => void onSuggest()}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {suggesting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Sparkles size={13} />
            )}
            {t('suggestMilestones.suggestWithAi')}
          </button>

          {aiNotConfigured && (
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} />
              {t('suggestMilestones.aiNotConfigured')}
            </div>
          )}

          {aiError && (
            <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} /> {aiError}
            </div>
          )}

          {notes && (
            <p className="text-xs t-muted bg-[var(--bg-input-soft)] border border-base rounded-lg px-2.5 py-1.5">
              {notes}
            </p>
          )}
        </div>

        {/* Revisión/edición (abajo) */}
        <div className="surface p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
              {t('suggestMilestones.suggestedMilestones')}
            </h3>
            {rows.length > 0 && (
              <button
                type="button"
                onClick={addRow}
                className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              >
                <Plus size={13} /> {t('suggestMilestones.add')}
              </button>
            )}
          </div>

          {rows.length === 0 ? (
            <p className="text-xs t-faint">
              {t('suggestMilestones.emptyHint')}
            </p>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider t-faint">
                  <tr>
                    <th className="text-left py-1">{t('suggestMilestones.col.milestone')}</th>
                    <th className="text-right w-24">{t('suggestMilestones.col.base')}</th>
                    <th className="text-right w-14">{t('suggestMilestones.col.pct')}</th>
                    <th className="text-left w-32">{t('suggestMilestones.col.date')}</th>
                    <th className="w-7" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const rowCents = parseEurosToCents(r.amountEur)
                    const rowPct =
                      dealAmountCents > 0 ? (rowCents / dealAmountCents) * 100 : 0
                    return (
                      <tr key={i} className="border-t border-base/40">
                        <td className="py-1 pr-2">
                          <input
                            value={r.title}
                            onChange={(e) => updateRow(i, { title: e.target.value })}
                            placeholder={t('suggestMilestones.milestoneDefault', { n: i + 1 })}
                            className={inputCls}
                          />
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            value={r.amountEur}
                            onChange={(e) => updateRow(i, { amountEur: e.target.value })}
                            placeholder="0,00"
                            className={`${inputCls} text-right`}
                            inputMode="decimal"
                          />
                        </td>
                        <td className="py-1 pr-2 text-right text-xs font-mono t-faint">
                          {dealAmountCents > 0 ? `${rowPct.toFixed(1)}%` : '—'}
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="date"
                            value={r.date}
                            onChange={(e) => updateRow(i, { date: e.target.value })}
                            className={inputCls}
                          />
                        </td>
                        <td className="py-1 text-right">
                          <button
                            type="button"
                            onClick={() => removeRow(i)}
                            className="t-faint hover:text-rose-700 dark:hover:text-rose-300"
                            title={t('suggestMilestones.deleteRow')}
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              <div className="text-xs">
                <p className={amountOk ? 't-faint' : 'text-amber-700 dark:text-amber-300'}>
                  {t('suggestMilestones.sum')}: {formatCents(sumCents)}
                  {dealAmountCents > 0 && ` · ${t('suggestMilestones.project')}: ${formatCents(dealAmountCents)}`}
                  {dealAmountCents > 0 && !amountOk && ` (${t('suggestMilestones.mismatch')})`}
                </p>
              </div>
            </>
          )}

          {createError && (
            <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} /> {createError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="button"
              disabled={!canCreate}
              onClick={() => void onCreate()}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? <Loader2 size={13} className="animate-spin" /> : null}
              {t('suggestMilestones.create')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
